// --------------------------------------------------------
// ATTACH CARE LINE - AttachCareLine
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAppDispatch } from '../../../..';
import CareLineDropdown from '../../../../components/dropdown/CareLineDropdown';
import { GenericSolidButton } from '../../../../components/UI/buttons/Button.styled';
import ContainerWithSidebar from '../../../../components/UI/containerWithSidebar/ContainerWithSidebar';
import { GeneralContainer, TextLink, Text, H2 } from '../../../../components/UI/generalLayout/Layout.styled';
import ScreenHeader from '../../../../components/UI/screenHeader/ScreenHeader';
import Colors from '../../../../constants/Colors';
import { getDescricaoByCodigoGender } from '../../../../enums/Genders';
import { Paths } from '../../../../enums/Paths';
import { handleBackendErrors } from '../../../../helpers/HelperFunctions';
import useResponsiveInfo from '../../../../hooks/useResponsiveInfo';
import CareLine from '../../../../models/core/careLine/CareLine';
import Category from '../../../../models/core/careLine/Category';
import Exam from '../../../../models/core/careLine/Exam';
import Procedure from '../../../../models/core/careLine/Procedure';
import VitalSign from '../../../../models/core/careLine/VitalSign';
import Person from '../../../../models/core/userRole/Person';
import * as careLineActions from '../../../../store/actions/careLine';
// --------------------------------------------------------
// ATTACH CARE LINE INTERFACE
// --------------------------------------------------------
/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */

export const CancelButton = styled(GenericSolidButton)<{isMobile?: boolean}>`
    background-color: white;
    border: 1px solid ${Colors.SPM500}; 
    color: ${Colors.SPM500};
    width: ${props=>props.isMobile? '100%':'200px'};
`

//style={{borderRadius: 8 }}
export const CareLineInfoContainer = styled.div<{ width?: number }>`
    width: ${props => props.width ? (props.width + 'px') : '100%'};
    padding: 2px 10px 2px 10px;
    background-color:${Colors.GRAY50};
    border-radius: 8px;

`
function AttachCareLine(): JSX.Element {
    // --------------------------------------------------------
    // ATTACH CARE LINE PRIVATE DECLARATIONS
    // --------------------------------------------------------
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { isMobile } = useResponsiveInfo();
    const { state } = useLocation();
    const { pessoa, agendamento }: any = state ?? new Person();
    const [selectedCareLine, setSelectedCareLine] = useState<CareLine | null>(null)
    const [errors, setErrors] = useState<any[]>([])

    const { careLines, allExams, allProcedures, allVitalSigns, allCategories } = useSelector((state: any) => state.careLine)


    const fetchCareLines = useCallback(async () => {
        await dispatch(careLineActions.fetchDropdownData())
        await dispatch(careLineActions.fetchActiveCareLines())
    }, [])

    const attachCareLine = useCallback(async ()=>{
        try{
            await dispatch(careLineActions.attachCareLineToPerson(selectedCareLine?.id ?? '', null, pessoa.idPessoa))
            onBackToProntuario();
        }catch(error: any){
            handleBackendErrors(error, setErrors)
        }
    }, [selectedCareLine, pessoa])

    const onAttachClick = ()=>{
        try{
            attachCareLine();
        }catch(error: any){
            handleBackendErrors(error, setErrors)
        }
    }

    useEffect(() => {
        fetchCareLines();
    }, [])

    const onBackToProntuario = () => navigate(Paths.Prontuario, { state: { pessoa: pessoa, agendamento: agendamento }});

    const getObjectFromList = (obj: any, list: any[]) => {
        const retorno = list.filter((c: Category) => { return c.id === obj.id })
        return retorno[0];
    }

    const getAgeText = (age: number | null) => {
        const retorno = age !== null ? age === 1 ? '0' + age + ' ano' : (age > 9 ? age + ' anos' : '0' + age + ' anos') : '';
        return retorno;
    }

    const getIntervalText = (interval: number | null) => {
        const retorno = interval !== null ? interval === 1 ? '0' + interval + ' mês' : (interval > 9 ? interval + ' meses' : '0' + interval + ' meses') : '';
        return retorno;
    }

    return <ContainerWithSidebar>
        <GeneralContainer>
            <TextLink color={Colors.SPM600} onClick={onBackToProntuario}>
                {'< Voltar para prontuário'}
            </TextLink>
            <ScreenHeader
                screenTitle={'Atribuir linha de cuidado'}
                rightButtonText={''}
                onRightButtonClick={() => { }}
                errors={errors}
                onCloseError={()=>{ setErrors([]) }}
                hasRightButton={false}
            />
            <H2>Selecionar Linha de Cuidado</H2>
            <CareLineDropdown
                value={selectedCareLine}
                onChange={setSelectedCareLine}
                items={careLines}
                containerStyle={{ width: isMobile ? '100%' : '40%' }}
                disabled={false}
                placeholder="Buscar linha de cuidado"
            />

            {selectedCareLine !== null && (<div style={{ marginTop: 40 }}>
                <H2>Idade e gênero</H2>

                <Text color={'#64748B'} style={{ fontSize: 14 }}>Faixa etária e gênero</Text>
                <div style={{ flexDirection: isMobile? 'column':'row', display: 'flex', gap: 10 }}>
                    {selectedCareLine.idadeMinima !== null && (<CareLineInfoContainer width={100}>
                        <Text>{getAgeText(selectedCareLine?.idadeMinima)}</Text>
                    </CareLineInfoContainer>)}
                    {selectedCareLine.idadeMaxima !== null && (<CareLineInfoContainer width={100}>
                        <Text>{getAgeText(selectedCareLine?.idadeMaxima)}</Text>
                    </CareLineInfoContainer>)}
                    <CareLineInfoContainer width={150}>
                        <Text>{selectedCareLine.sexo === null ? 'Ambos gêneros' : getDescricaoByCodigoGender(selectedCareLine.sexo)}</Text>
                    </CareLineInfoContainer>
                </div>

                {selectedCareLine.categorias.length > 0 && (
                <>
                    <H2>Tipo de profissional</H2>
                    {selectedCareLine.categorias.map((categoria: Category, index: number) => {
                        const category = getObjectFromList(categoria, allCategories);
                        return <div style={{ flexDirection: isMobile? 'column':'row', display: 'flex', gap: 10, marginBottom: 10 }}>
                            <div>
                                {index === 0 && (<Text color={'#64748B'} style={{ fontSize: 14 }}>Tipo de Profissional</Text>)}
                                <CareLineInfoContainer width={250}>
                                    <Text>{category.nome}</Text>
                                </CareLineInfoContainer>
                            </div>
                            <div>
                                {index === 0 && (<Text color={'#64748B'} style={{ fontSize: 14 }}>Intervalo</Text>)}
                                <CareLineInfoContainer width={200}>
                                    <Text>{getIntervalText(categoria.intervaloMeses)}</Text>
                                </CareLineInfoContainer>
                            </div>
                        </div>
                    })}
                </>
                )}

                {selectedCareLine.sinaisVitais.length > 0 && (
                <>
                    <H2>Sinais vitais</H2>
                    <Text color={'#64748B'} style={{ fontSize: 14 }}>Lista de Sinais Vitais</Text>
                    {selectedCareLine.sinaisVitais.map((sinalVital: VitalSign) => {
                        const vitalSign = getObjectFromList(sinalVital, allVitalSigns);
                        return <div style={{ flexDirection: isMobile? 'column':'row', display: 'flex', gap: 10, marginBottom: 10 }}>
                            <div>
                                <CareLineInfoContainer width={250}>
                                    <Text>{vitalSign.nome}</Text>
                                </CareLineInfoContainer>
                            </div>
                        </div>
                    })}
                </>
                )}

                {selectedCareLine.exames.length > 0 && (
                <>
                    <H2>Exames</H2>
                    {selectedCareLine.exames.map((exame: Exam, index: number) => {
                        const exam = getObjectFromList(exame, allExams);
                        return <div style={{ flexDirection: isMobile? 'column':'row', display: 'flex', gap: 10, marginBottom: 10 }}>
                            <div>
                                {index === 0 && (<Text color={'#64748B'} style={{ fontSize: 14 }}>Lista de exames</Text>)}
                                <CareLineInfoContainer width={250}>
                                    <Text>{exam.nome}</Text>
                                </CareLineInfoContainer>
                            </div>
                            <div>
                                {index === 0 && (<Text color={'#64748B'} style={{ fontSize: 14 }}>Intervalo</Text>)}
                                <CareLineInfoContainer width={100}>
                                    <Text>{getIntervalText(exame.intervaloMeses)}</Text>
                                </CareLineInfoContainer>
                            </div>
                            <div>
                                {index === 0 && (<Text color={'#64748B'} style={{ fontSize: 14 }}>Formato do resultado</Text>)}
                                <CareLineInfoContainer width={150}>
                                    <Text>{exame.formato}</Text>
                                </CareLineInfoContainer>
                            </div>
                        </div>
                    })}
                    </>
                )}

                {selectedCareLine.procedimentos.length > 0 && (
                <>
                    <H2>Procedimentos</H2>
                    {selectedCareLine.procedimentos.map((procedimento: Procedure, index: number) => {
                        const procedure = getObjectFromList(procedimento, allProcedures);
                        return <div style={{ flexDirection: isMobile? 'column':'row', display: 'flex', gap: 10, marginBottom: 10 }}>
                            <div>
                                {index === 0 && (<Text color={'#64748B'} style={{ fontSize: 14 }}>Lista de procedimentos</Text>)}
                                <CareLineInfoContainer width={250}>
                                    <Text>{procedure.nome}</Text>
                                </CareLineInfoContainer>
                            </div>
                            <div>
                                {index === 0 && (<Text color={'#64748B'} style={{ fontSize: 14 }}>Intervalo</Text>)}
                                <CareLineInfoContainer width={100}>
                                    <Text>{getIntervalText(procedimento.intervaloMeses)}</Text>
                                </CareLineInfoContainer>
                            </div>
                        </div>
                    })}
                </>)}

                <div style={{ gap: 10, flexDirection: isMobile? 'column':'row', display: 'flex', marginTop: 40, marginBottom: isMobile? 40 : 0 }}>
                    <CancelButton onClick={onBackToProntuario} isMobile={isMobile}>Cancelar</CancelButton>
                    <GenericSolidButton color={Colors.SPM600} onClick={onAttachClick}>Vincular linha de cuidado</GenericSolidButton>
                </div>
            </div>)}

        </GeneralContainer>
    </ContainerWithSidebar>;
}

export default AttachCareLine;
